import React from 'react'
import R from 'ramda'
import PropTypes from 'prop-types'
import {
  Form,
  Button,
  Label
} from 'semantic-ui-react'
import {
  escapeRE
} from '_utils'

import {
  AUTH_API_URL
} from '_constants'

import AutoComplete from '_views/autocomplete/url'
import SuggestionItem from '_views/suggestionItem'
import SuggestionItems from '_views/suggestionItems'
import Gravatar from '_views/gravatar'

import BaseNumberField from '_forms/baseNumber'

const getAcUrl = (term) => `${AUTH_API_URL}/users`

const formatSelection = user => {
  const _id = R.prop('_id', user)
  const display = R.prop('displayname', user)
  const email = R.prop('email', user)
  return (
    <Label style={{display: 'inline-block'}} key={'sel_' + _id}>
      <div>{ display }</div>
      <div>{ email }</div>
      <div><small>{ _id }</small></div>
    </Label>
  )
}

const formatSuggestion = (user, term) => {
  const getDisplay = R.prop('displayname')
  const getEmail = R.prop('email')
  const getId = R.prop('_id')
  const getUsername = R.prop('username')
  const getDescription = R.prop('description')
  // these all need filtering
  const getFsRef = R.path(['activated_info', 'fastspring', 'reference'])
  const getFsSubRef = R.path(['activated_info', 'fastspring', 'subscription', 'reference'])
  const getFsFullName = R.path(['activated_info', 'fastspring', 'customer', 'full_name'])
  const getFsEmail = R.path(['activated_info', 'fastspring', 'customer', 'email'])
  const getFsMax6Sn = R.path(['activated_info', 'fastspring', 'surveys', 'max6_serial'])
  const getLicSn = R.path(['activated_info', 'serial_id'])
  const getAc = R.path(['activated_info', 'ac_id'])
  const getNote = R.prop('note')

  const getLics = R.compose(
    R.defaultTo([]),
    R.prop('licenses')
  )
  const getNotes = R.compose(
    R.defaultTo([]),
    R.prop('notes')
  )

  const tReg = {
    regex: new RegExp(escapeRE(term), 'i'),
    term
  }
  return (
    <div key={'sug_' + user._id} >
      <Gravatar gravatar={R.prop('gravatar', user)} size={30} floated='left' circular />

      <SuggestionItem label='email' text={getEmail(user)} tReg={tReg} always={true} />
      <SuggestionItem label='display' text={getDisplay(user)} tReg={tReg} always={true} />

      <SuggestionItem label='id' text={getId(user)} tReg={tReg} />

      <SuggestionItem label='username' text={getUsername(user)} tReg={tReg} />
      <SuggestionItem label='description' text={getDescription(user)} tReg={tReg} />

      <SuggestionItems label='fs ref' list={getLics(user)} pathFn={getFsRef} tReg={tReg} />
      <SuggestionItems label='fs sub ref' pathFn={getFsSubRef} list={getLics(user)} tReg={tReg} />
      <SuggestionItems label='fs name' pathFn={getFsFullName} list={getLics(user)} tReg={tReg} />
      <SuggestionItems label='fs email' pathFn={getFsEmail} list={getLics(user)} tReg={tReg} />
      <SuggestionItems label='fs serial' pathFn={getFsMax6Sn} list={getLics(user)} tReg={tReg} />
      <SuggestionItems label='serial' pathFn={getLicSn} list={getLics(user)} tReg={tReg} />
      <SuggestionItems label='ac' pathFn={getAc} list={getLics(user)} tReg={tReg} />
      <SuggestionItems label='note' pathFn={getNote} list={getNotes(user)} tReg={tReg} />
    </div>
  )
}

const InstitutionProfile = ({
  profile,
  dirty,
  onChange,
  onAdminChange,
  onCancel,
  onSubmit,
  readOnly
}) => {

  return (
    <Form size='tiny' onSubmit={onSubmit}>
      <Form.Group>
        <Form.Input readOnly={ readOnly } width={8} label='Name' name='name' placeholder='Name' value={ profile.name || '' } onChange={onChange} />
        <Form.Input
          width={8}
          label='Description'
          name='description'
          placeholder='Description'
          value={ profile.description || '' }
          readOnly={ readOnly }
          onChange={onChange}
        />
      </Form.Group>
      <Form.Group>
        <Form.Input
          width={5}
          label='Contact Name'
          name='contact_name'
          placeholder='Name (required)'
          value={ profile.contact_name || '' }
          readOnly={ readOnly }
          onChange={onChange}
        />
        <Form.Input
          width={5}
          label='Contact Email'
          name='contact_email'
          placeholder='Email (required)'
          value={ profile.contact_email || '' }
          onChange={onChange}
          readOnly={ readOnly }
          type='email' />
        <Form.Input
          width={6}
          label='Contact Phone'
          name='contact_phone'
          placeholder='Phone'
          value={ profile.contact_phone || '' }
          readOnly={ readOnly }
          onChange={onChange}
        />
      </Form.Group>
      <Form.Group>
        <Form.Field width={5}>
          <Form.Input
            label='Xero ID'
            name='xero_id'
            placeholder='Xero ID (please enter the UUID from Xero)'
            value={ profile.xero_id || '' }
            onChange={onChange}
            readOnly={ readOnly }
          />
          {profile.xero_id ? (
            <p style={{ paddingTop: "8px" }}>
              <a href={`https://go.xero.com/Contacts/View/${profile.xero_id}`} target="_blank" rel="noopener noreferrer">
                To Xero Account »
              </a>
            </p>
          ) : null}
        </Form.Field>
        <Form.Field width={5}>
          <Form.Input
            label='Capsule Id'
            name='capsule_id'
            placeholder='Capsule ID (please enter the ID from Capsule)'
            value={ profile.capsule_id || '' }
            readOnly={ readOnly }
            onChange={onChange}
          />
          {profile.capsule_id ? (
            <p style={{ paddingTop: "8px" }}>
              <a href={`https://cycling74.capsulecrm.com/party/${profile.capsule_id}`} target="_blank" rel="noopener noreferrer">
                To Capsule Account »
              </a>
            </p>
          ) : null}
        </Form.Field>
        <Form.Input
          width={6}
          label='Netsuite Id'
          name='netsuite_id'
          placeholder='Netsuite id (please enter the former netsuite id)'
          value={ profile.netsuite_id || '' }
          readOnly={ readOnly }
          onChange={onChange}
        />
      </Form.Group>
      <Form.Group>
        <Form.Field width={8}>
          <label>Administrators</label>
          <AutoComplete
            selections={profile.administrators || []}
            onSelectionChange={onAdminChange}
            getUrl={getAcUrl}
            uniqField='_id'
            mapHttpBodyToSuggestions={R.prop('results')}
            formatSelection={formatSelection}
            formatSuggestion={formatSuggestion}
          />
        </Form.Field>
        <BaseNumberField
          width={ 8 }
          min={ 0 }
          labelTxt='Total old licenses available for upgrade'
          popupTxt='How many seats did they have prior to Max 7 and are available for upgrade purchase?'
          name='oldlicense_count'
          value={ profile.oldlicense_count || 0 }
          readOnly={ readOnly }
          onChange={ onChange }
        />
      </Form.Group>
      {
        readOnly ? null : (
          <Button.Group>
            <Button disabled={!dirty} onClick={onCancel}>Cancel</Button>
            <Button.Or />
            <Button positive={dirty} disabled={!dirty} type="submit">Save</Button>
          </Button.Group>
        )
      }
    </Form>
  );
}

InstitutionProfile.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdminChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  dirty: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired
}

export default InstitutionProfile



// WEBPACK FOOTER //
// ./webAdmin/forms/institutionProfileForm.js